import type { FmTableCellValue, FmTableLayout } from '../../../types/fm-table';
import type { FmActionConfirmationDescriptor } from '../FmConfirm';
import type {
  FmStoryCashflowPeriod,
  FmStoryCorridorRow,
} from './plotChartFields.types';

/**
 * Field shapes the typed elements carry: metrics, notices, chart data, ranked
 * rows, tables, actions, timelines and comparisons. They are payload parts,
 * never descriptors; an element or a section places them.
 */
export type FmStoryAccentTone = 'accent' | 'teal' | 'amber' | 'sky' | 'rose' | 'neutral';

/**
 * A served headline number for a key figure: the formatted value, its unit in
 * its own slot, and colour only through a declared semantic status.
 */
export type FmStoryMetric = {
  label: string;
  unit?: string;
  detail?: string;
  status?: 'good' | 'caution' | 'loss';
} & ({ value: string; unavailableReason?: never } | { value: null; unavailableReason: string });

export interface FmStoryBadge {
  label: string;
  tone?: FmStoryAccentTone;
}

export type FmStoryNoticeTone = 'info' | 'warning' | 'error' | 'success';

/**
 * A data-gated advisory banner surfaced above a section body. Pages describe the
 * notice (identity, tone, copy, test id) only when the underlying server data
 * warrants it; the renderer owns the banner presentation. Never emit a notice on
 * absent/placeholder data — NO-FALLBACK.
 */
export interface FmStoryNotice {
  /** Stable identity used for the React key. */
  id: string;
  tone: FmStoryNoticeTone;
  /** Short banner heading. */
  title: string;
  /** Plain-language explanation of what the notice means. */
  description: string;
  /** Explicit test id applied to the rendered banner. */
  testId: string;
}

export interface FmStoryChapter {
  id: string;
  label: string;
  meta?: string;
}

export interface FmStoryPoint {
  label: string;
  value: number;
  tone?: FmStoryAccentTone;
}

export type FmStoryChartDataValue = string | number | null | undefined;

interface FmStoryDataChartBase {
  data: readonly Record<string, FmStoryChartDataValue>[];
  xKey: string;
  activeKey?: string;
}

/** Cartesian story charts title their category axis; the value axis carries the unit. */
interface FmStoryCartesianChartBase extends FmStoryDataChartBase {
  xAxisLabel: string;
}

type FmStoryChartMembers =
  | (FmStoryCartesianChartBase & {
      kind: 'species-share';
      series: readonly FmStoryChartSeries[];
      mode: 'species' | 'assortment';
    })
  | (FmStoryCartesianChartBase & {
      kind: 'ranked-bar' | 'value-growth';
      valueKey: string;
      /** Names the served value; a data key is never printed in its place. */
      valueLabel: string;
      /**
       * A second served value per category read against the first (a claim
       * beside the modelled figure), drawn as the quieter reference bar beside
       * each value bar. A missing reference cell stays a gap.
       */
      reference?: FmStoryChartReference;
    })
  | (FmStoryDataChartBase & {
      kind: 'share-donut';
      valueKey: string;
      toneKey?: string;
      colorMode?: 'tone' | 'species';
      speciesCodeKey?: string;
    })
  | (FmStoryCartesianChartBase & {
      kind: 'growth-trajectory';
      render?: 'line' | 'area' | 'stacked-area';
      metric: 'volume' | 'height' | 'diameter' | 'basalArea' | 'stems';
    } & (
        | { series: readonly FmStoryChartSeries[]; valueKey?: never; valueLabel?: never }
        | { series?: never; valueKey: string; valueLabel: string }
      ))
  | (FmStoryDataChartBase & {
      /** Generic dated series. Missing values remain gaps and are never coerced to zero. */
      kind: 'time-series';
      series: readonly [FmStoryChartSeries, ...FmStoryChartSeries[]];
      /** Suppress the separate key when direct line labels carry the series identity. */
      legend?: 'auto' | 'none';
      onRangeChange?: (range: readonly [string, string]) => void;
    })
  | {
      kind: 'diameter-distribution';
      xAxisLabel: string;
      /** Stems per species and class; the stems that stay when a cut is served. */
      distributions: readonly FmStoryDiameterDistributionEntry[];
      /** The served removed stems per species and class; drawn in cut red on top. */
      removedDistributions?: readonly FmStoryDiameterDistributionEntry[];
      /** Legend label of the cut series; required when removed stems are served. */
      cutLabel?: string;
      /** Draws the staying stems as one series with this label (the buyer PDF's picture); needs a cut. */
      keptLabel?: string;
    }
  | {
      /** Served euro cash flow per period; the currency ticks carry the euro. */
      kind: 'cashflow';
      xAxisLabel: string;
      /** Names the plotted quantity on the value axis. */
      valueLabel: string;
      presentation: 'signed-net-bars' | 'grouped-bars-net-line';
      seriesLabels: { income: string; cost: string; net: string };
      periods: readonly FmStoryCashflowPeriod[];
    }
  | {
      /** Residual basal area per reviewed cutting against its typical band and legal floor. */
      kind: 'corridor-rows';
      rows: readonly FmStoryCorridorRow[];
      bandLabel: string;
      floorLabel: string;
      residualLabel: string;
    };

type FmStoryChartUnionKeys<Union> = Union extends Union ? keyof Union : never;
type FmStrictChartUnion<Member, Whole> = Member extends Member
  ? Member & Partial<Record<Exclude<FmStoryChartUnionKeys<Whole>, keyof Member>, never>>
  : never;

export type FmStoryChart = FmStrictChartUnion<FmStoryChartMembers, FmStoryChartMembers>;

/** The reference value a ranked bar is read against, named in the owner's words. */
export interface FmStoryChartReference {
  valueKey: string;
  valueLabel: string;
}

export interface FmStoryChartSeries {
  code: number | string;
  label: string;
  tone?: FmStoryAccentTone;
  /** Optional row key when several semantic series share one species colour. */
  dataKey?: string;
  /** Optional stack group; equal keys stack while distinct keys render side by side. */
  stackKey?: string;
  /** Comparison meaning; the renderer owns visual emphasis for each role. */
  role?: 'reference' | 'result';
}

export interface FmStoryDiameterBin {
  diameter_cm: number;
  n_per_ha: number;
}

export interface FmStoryDiameterDistributionEntry {
  /** Metsäkeskus species code; colour is keyed by it. */
  speciesCode: number;
  /** Localized species name for the legend. */
  label: string;
  bins: readonly FmStoryDiameterBin[];
}

export interface FmStoryRankedRow {
  key?: string;
  rank: string;
  label: string;
  value: string;
  score?: number;
  delta?: string;
  /** A secondary line under the label; it never repeats the value. */
  status?: string;
  /** Species code: a swatch in its map and chart colour (`charts/palette.ts`). */
  speciesCode?: number;
  /** `accent` marks the row; ranking alone never highlights a row. */
  tone?: FmStoryAccentTone;
  selected?: boolean;
  onClick?: () => void;
  testId?: string;
}

export interface FmStoryRankedTableLens {
  id: string;
  label: string;
  disabled?: boolean;
}

export interface FmStoryRankedTableColumn {
  key: string;
  header: string;
  role?: FmStoryTableColumnRole;
  numeric?: boolean;
}

export type FmStoryRankedTableValue = string | number | null | undefined;

export interface FmStoryRankedTableRow {
  id: string;
  values: Record<string, FmStoryRankedTableValue>;
  ariaLabel?: string;
  testId?: string;
}

export interface FmStoryRankedTableInsightItem {
  id: string;
  title: string;
  body: string;
  tone?: FmStoryAccentTone;
}

export interface FmStoryRankedTableInsight {
  objective: string;
  lensLabel?: string;
  lenses?: FmStoryRankedTableLens[];
  activeLensId?: string;
  onLensChange?: (lensId: string) => void;
  columns: FmStoryRankedTableColumn[];
  rows: FmStoryRankedTableRow[];
  previewLimit?: number;
  showAllLabel?: string;
  showLessLabel?: string;
  selectedRowId?: string;
  /**
   * The caller owns selection outright — including the moment nothing is
   * selected — and the renderer must never substitute its own idea of a
   * default row.
   *
   * Without this flag, `selectedRowId: undefined` is ambiguous: it is how an
   * uncontrolled caller (the story gallery) signals "manage selection
   * yourself", and it is also how a controlled caller (stand-detail's
   * futures shortlist) spells "nothing is selected right now" — the
   * descriptor validator rejects `selectedRowId: null` as not a non-empty
   * string, so omission is the only legal way to say that. Collapsing both
   * meanings into the same `undefined` let the renderer's own local fallback
   * state re-select a stale row — a ranked candidate rendered as though it
   * were the owner's choice. `selectionControlled: true` disambiguates: the
   * renderer honours `selectedRowId` exactly, including `undefined` meaning
   * "no row is current", and never reaches for `rows[0]` or a captured prior
   * selection instead.
   */
  selectionControlled?: boolean;
  onRowSelect?: (rowId: string) => void;
  insights: FmStoryRankedTableInsightItem[];
  insightsLabel?: string;
  assumptions?: string;
  unavailableReason?: string;
  emptyLabel?: string;
  tableCaption?: string;
  testId?: string;
}

export interface FmStoryValueExplanation {
  label: string;
  value?: string;
  detail: string;
  tone?: FmStoryAccentTone;
}

interface FmStoryActionBase {
  label: string;
  testId?: string;
}

export type { FmActionConfirmationDescriptor as FmStoryActionConfirmation } from '../FmConfirm';

/** A visible story action always has at least one executable target. */
export type FmStoryAction = FmStoryActionBase &
  (
    | { href: string; onClick?: () => void; confirmation?: FmActionConfirmationDescriptor }
    | { href?: never; onClick: () => void; confirmation?: FmActionConfirmationDescriptor }
    | { disabled: true; disabledReason: string; href?: never; onClick?: never }
  );

export type FmStoryTableColumnRole =
  | 'label'
  | 'numeric'
  | 'money'
  | 'delta'
  | 'status'
  | 'source'
  | 'metadata';

export interface FmStoryTableColumn {
  key: string;
  header: string;
  numeric?: boolean;
  sortable?: boolean;
  role?: FmStoryTableColumnRole;
  /** A shorter header for the labelled-cell tier, where a full sentence does not fit. */
  mobileLabel?: string;
  /** Dropped from the labelled-cell tier: a rank, or a label that repeats its own value. */
  mobileHidden?: boolean;
}

/**
 * One served row. A structured cell (signed money, species swatch, operation
 * tag) renders through the table's own cell vocabulary; a scalar prints as is.
 */
export interface FmStoryTableRow {
  key: string;
  [field: string]: FmTableCellValue | undefined;
}

export interface FmStoryTableAction {
  id: string;
  label: string;
  intent?: 'primary' | 'secondary' | 'inspect' | 'confirm' | 'export' | 'destructive';
  disabledReason?: string;
  /** Read-only actions stay usable while the host inhibits mutations. */
  availableWhileInhibited?: boolean;
  confirm?: {
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel: string;
  };
  onSelect: () => void;
  testId?: string;
}

export interface FmStoryTableSelectorControl {
  kind: 'selector';
  id: string;
  label: string;
  value: string;
  variant?: 'segmented' | 'radio';
  options: Array<{
    value: string;
    label: string;
    disabled?: boolean;
    testId?: string;
  }>;
  onChange?: (value: string) => void;
  testId?: string;
}

export interface FmStoryTableToggleControl {
  kind: 'toggle';
  id: string;
  label: string;
  checked: boolean;
  onChange?: (checked: boolean) => void;
  testId?: string;
}

export type FmStoryTableControl = FmStoryTableSelectorControl | FmStoryTableToggleControl;

export interface FmStoryContextControlOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * A page-neutral selector for changing the served evidence context. The
 * renderer owns its concrete form control and places it after provenance.
 */
export interface FmStoryContextControl {
  id: string;
  label: string;
  /** Null keeps every option visibly unselected while recovery remains possible. */
  value: string | null;
  presentation: 'select' | 'segmented' | 'radio';
  options: readonly [FmStoryContextControlOption, ...FmStoryContextControlOption[]];
  onChange?: (value: string) => void;
  disabledReason?: string;
  testId?: string;
}

export interface FmStoryTableDescriptor {
  caption: string;
  columns: FmStoryTableColumn[];
  rows: FmStoryTableRow[];
  /** BCP-47 locale for structured currency and number cells. */
  locale?: string;
  /** Repeated dense rows use the table's operational row layout. */
  layout?: FmTableLayout;
  /**
   * How the table reads on a phone. Default `labelled-cells` turns each row
   * into label/value cells; `table` keeps the columns for a short matrix of
   * a few numeric columns, which reads as a grid at 390 px.
   */
  mobileLayout?: 'table' | 'labelled-cells';
  /** Localized label for an empty cell. */
  emptyCellLabel?: string;
  controls?: FmStoryTableControl[];
  previewLimit?: number;
  showAllLabel?: string;
  showLessLabel?: string;
  emptyText?: string;
  tableTestId?: string;
  rowTestId?: (row: FmStoryTableRow) => string | undefined;
  rowAriaLabel?: (row: FmStoryTableRow) => string | undefined;
  rowActions?: (row: FmStoryTableRow) => FmStoryTableAction[];
  /** Stable active row projected from route/report selection. */
  selectedRowId?: string;
  /** Optional DS-owned row selection callback. */
  onRowSelect?: (rowId: string) => void;
  actionHeader?: string;
  actionLabel?: string;
  actionOverflowLabel?: string;
}

export type {
  FmStoryCorridorPoint,
  FmStoryCorridorRateSeries,
  FmStoryCorridorRefusal,
  FmStoryCorridorStatutoryFloor,
  FmStoryCorridorTrajectory,
  FmStoryCorridorTrajectoryPath,
  FmStoryCorridorTrajectoryPoint,
} from '../charts/FmCorridorTrajectory.types';

export type FmStoryTimelinePhase = 'past' | 'current' | 'future';

export interface FmStoryTimelineEvent {
  year: string;
  label: string;
  detail: string;
  /** Past/current/future semantics for the canonical FmTimeline primitive. */
  phase?: FmStoryTimelinePhase;
  /** Visible owner-readable phase label, such as Reported, Current, or Simulated. */
  phaseLabel?: string;
  /** Short source/status row rendered below the event title. */
  meta?: string;
  /** Optional additional evidence line rendered inside the timeline item. */
  content?: string;
  tone?: FmStoryAccentTone;
  testId?: string;
}

export interface FmStoryComparisonItem {
  id: string;
  label: string;
  summary?: string;
  status?: string;
  tone?: FmStoryAccentTone;
  metrics?: FmStoryMetric[];
  testId?: string;
}

export interface FmStoryComparisonCriterion {
  key: string;
  label: string;
  detail?: string;
  values: Record<string, string>;
  winnerItemId?: string;
  tone?: FmStoryAccentTone;
}

export interface FmStoryComparisonGroup {
  id: string;
  label: string;
  summary?: string;
  criteria: FmStoryComparisonCriterion[];
}

/**
 * The matrix compares at most this many items side by side; more would not fit
 * a readable row, so the renderer shows the first ones and a caller whose drill
 * selection must reach every item checks its count against this.
 */
export const FM_COMPARISON_MAX_ITEMS = 4;

export interface FmStoryComparisonMatrix {
  presentation?: 'story-table' | 'table-only';
  items: FmStoryComparisonItem[];
  groups: FmStoryComparisonGroup[];
  activeGroupId?: string;
  groupLabel?: string;
  criterionLabel?: string;
  drilledItemId?: string;
  onGroupChange?: (groupId: string) => void;
  onDrillItem?: (itemId: string) => void;
  emptyLabel?: string;
  testId?: string;
}
